import React, { useEffect, useRef, useState } from 'react';
import { Alert, Snackbar } from '@mui/material';
import { 
  usePostPaymentsPaynowStatusMutation,
  usePostPaymentsPaynowCallbackMutation
} from '../../redux/residentPortalApi';
import { parsePaynowReturnParams, clearPaynowReturnParams } from '../../utils/paymentUtils';
import { isNative } from '../../utils/platform';

/**
 * Component that handles payment returns from PayNow
 * This component should be mounted at the app level to detect when a user
 * returns from a PayNow payment and verify the payment status
 */
const PaymentReturnHandler: React.FC = () => {
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const handledReferencesRef = useRef<Set<string>>(new Set());
  
  const [checkPaynowStatus] = usePostPaymentsPaynowStatusMutation();
  const [sendPaynowCallback] = usePostPaymentsPaynowCallbackMutation();
  
  useEffect(() => {
    let removeListener: (() => void) | undefined;

    const processReturn = (incomingUrl?: string, shouldClearCurrentUrl: boolean = false) => {
      const returnParams = parsePaynowReturnParams(incomingUrl);
      if (!returnParams?.reference) return;

      if (handledReferencesRef.current.has(returnParams.reference)) {
        return;
      }

      handledReferencesRef.current.add(returnParams.reference);

      if (shouldClearCurrentUrl) {
        clearPaynowReturnParams();
      }

      if (
        returnParams.status === 'paid' ||
        returnParams.status === 'cancelled' ||
        returnParams.status === 'failed'
      ) {
        console.log('Received direct status from PayNow:', returnParams.status);
      }

      verifyPayment(returnParams.reference, returnParams);
    };

    // Browser flow using query params on current URL.
    processReturn(undefined, true);

    // Native flow using appUrlOpen deep-link events.
    if (isNative()) {
      import('@capacitor/app')
        .then(({ App }) => App.addListener('appUrlOpen', ({ url }) => {
          processReturn(url, false);
        }))
        .then((listener) => {
          removeListener = () => {
            void listener.remove();
          };
        })
        .catch((error) => {
          console.warn('Could not attach native deep-link listener:', error);
        });
    }

    return () => {
      if (removeListener) {
        removeListener();
      }
    };
  }, []);

  const verifyPayment = async (
    reference: string,
    returnParamsOverride?: ReturnType<typeof parsePaynowReturnParams>
  ) => {
    try {
      // Get all URL parameters for the callback
      const returnParams = returnParamsOverride ?? parsePaynowReturnParams();
      
      // First, send the callback data to the API endpoint
      // This ensures the server receives the callback even if the client-side verification fails
      if (returnParams?.reference && returnParams.status && returnParams.hash) {
        try {
          await sendPaynowCallback({
            body: {
              reference: returnParams.reference,
              status: returnParams.status,
              pollurl: returnParams.pollUrl,
              hash: returnParams.hash,
            },
          }).unwrap();

          console.log('Sent callback data to API endpoint');
        } catch (callbackError) {
          console.error('Error sending callback to API:', callbackError);
          // Continue with client-side verification even if the callback fails
        }
      }
      
      // Check payment status with the backend
      const statusResponse = await checkPaynowStatus({
        body: { reference }
      }).unwrap();
      
      console.log('Payment return verification:', statusResponse);
      
      if (statusResponse.paid && (statusResponse.topUpStatus === 'failed' || statusResponse.topUpError)) {
        setErrorMessage(`Payment received, but the meter top-up failed: ${statusResponse.topUpError || 'unknown error'}. Please contact support.`);
        setShowError(true);
      } else if (statusResponse.paid) {
        // Server already marks the payment succeeded and runs the success flow during the status check.
        setShowSuccess(true);
      } else if (statusResponse.status === 'cancelled' || statusResponse.status === 'failed') {
        // Payment failed
        setErrorMessage('Payment was cancelled or failed. Please try again.');
        setShowError(true);
      } else {
        // Payment is still pending
        setErrorMessage('Payment is still being processed. Please check your account later.');
        setShowError(true);
      }
    } catch (error) {
      console.error('Error verifying payment:', error);
      setErrorMessage('Could not verify payment status. Please contact support.');
      setShowError(true);
    }
  };
  
  return (
    <>
      {/* Success notification */}
      <Snackbar 
        open={showSuccess} 
        autoHideDuration={6000} 
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setShowSuccess(false)}>
          Payment successful! Your transaction has been completed.
        </Alert>
      </Snackbar>
      
      {/* Error notification */}
      <Snackbar 
        open={showError} 
        autoHideDuration={6000} 
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setShowError(false)}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default PaymentReturnHandler;